import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "DomainKeys Identified Mail (DKIM) adds public key cryptography to the Simple Mail Transfer Protocol (SMTP). Email for the domain is digitally signed by the sending mail server using the private key for the domain. Receiving mail servers use the public key from the domain's DKIM record to validate that messages have not been tampered with.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DKIM is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc6376",
    rel: "noopener noreferrer"
  }, "RFC 6376"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dkim-a-practical-guide/dns-dkim-records.jpg",
    alt: "DKIM adds cryptography to email",
    title: "DKIM adds cryptography to email",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("DKIM adds cryptography to email. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "How DKIM works", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "A public key is published in a DNS DKIM record for the domain. The corresponding private key is used to sign email messages sent from the domain. Since the public key can be looked up in the DNS, all receiving mail servers can retrieve the public key from the DNS and use it to validate email signatures for the domain.", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("h3", null, "Signing outgoing messages", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Each email sent from the domain is signed just before the message is transmitted. To sign each email, the sending Mail Transfer Agent (MTA) normalizes the message following the rules in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc6376",
    rel: "noopener noreferrer"
  }, "RFC 6376"),
  /*#__PURE__*/_createTextVNode(". Then it creates a hash of the normalized email and uses the domain's private key to generate a cryptographic signature of the hash. The signature is inserted into the email message in the "),
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(" header field.")
], -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h3", null, "Validating received messages", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("When email is received, the receiving mail server examines the "),
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(" header field and performs a DNS lookup to retrieve the DKIM record for the domain. The DKIM record contains the public key for the domain. The receiving mail server normalizes the message and computes the hash, and then uses the domain's public key from the DKIM record to cryptographically validate that the email message has not been altered.")
], -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h3", null, "DMARC is required to prevent unsigned mail", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "DMARC policy requires that all email from the domain pass both SPF and DKIM authentication checks. Unsigned mail from spammers would be rejected or placed in quarantine.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Yes, a domain may have multiple DKIM records. Each DKIM record publishes the public key for a different private key.")
], -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Multiple DKIM records are common. DKIM uses \"selectors\" to allow multiple DKIM records (see "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc6376#section-3.1",
    rel: "noopener noreferrer"
  }, "RFC 6376 section 3.1"),
  /*#__PURE__*/_createTextVNode(").")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "There are two reasons why a domain might have multiple DKIM records:", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Multiple email providers"),
    /*#__PURE__*/_createTextVNode(": A domain may use more than one email provider, for example, MailChimp and Google Workspaces. Each provider will have their own private and public keys for DKIM.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Key rollover"),
    /*#__PURE__*/_createTextVNode(": It is a good security practice to periodically roll the DKIM key for a domain. When keys are rolled, there will be a short period of time when the old key and the new key are both published in different DKIM records.")
  ])
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h2", null, "The DKIM record format", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("h3", null, "Where are DKIM records stored?", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DKIM records are stored in the DNS for a domain using the label "),
  /*#__PURE__*/_createElementVNode("code", null, "_domainkey"),
  /*#__PURE__*/_createTextVNode(" preceded by an optional selector. If no selector is being used, the DKIM record for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" would be at "),
  /*#__PURE__*/_createElementVNode("code", null, "_domainkey.example.org"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("However, a DKIM record at "),
  /*#__PURE__*/_createElementVNode("code", null, "_domainkey.example.org"),
  /*#__PURE__*/_createTextVNode(" would be unusual. DKIM records are usually published with an additional label called a selector. A DKIM selector is a short text string that identifies, or \"selects\", among multiple DKIM records.")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The selector is the first label in the name where the DKIM record is stored in the DNS. If the selector for one of the DKIM records for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" is "),
  /*#__PURE__*/_createElementVNode("code", null, "blue"),
  /*#__PURE__*/_createTextVNode(" then the DKIM record would be at "),
  /*#__PURE__*/_createElementVNode("code", null, "blue._domainkey.example.org"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, "Many email providers who support DKIM instruct their customers to insert a CNAME record instead of a DKIM record into the DNS zone for the domain. The target of the CNAME is a DKIM record in TXT format, usually in a DNS zone owned by the email provider.", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "This is done because DKIM records can change, for example when the DKIM keys are rolled. If the customer uses a CNAME, then the customer's domain doesn't have to be changed at all. The key rollover DKIM record change can be performed by the email provider. This greatly reduces the administrative burden on the customer.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("h3", null, "DKIM format", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "DKIM records are TXT record that contain tag/value pairs. The tag's name is followed by an equals sign and then the tag's value. Tags are separated by semicolons. This format is identical to the format used in DMARC records.", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Below is a current DKIM record for Google. This DKIM record has the selector "),
  /*#__PURE__*/_createElementVNode("code", null, "20210112"),
  /*#__PURE__*/_createTextVNode(". This is probably the date the key was generated. The \"k\" tag says that this is an RSA key. All mail servers must support the \"rsa\" key type. The long key string is omitted for brevity:")
], -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("pre", null, "20210112._domainkey.google.com. 3600 TXT \"v=DKIM1; k=rsa; p={key string omitted}\"", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This DKIM record will be applied to any email message sent by Google whose "),
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(" specifies the "),
  /*#__PURE__*/_createElementVNode("code", null, "20210112"),
  /*#__PURE__*/_createTextVNode(" selector.")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("h3", null, "DKIM tags", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "DKIM tags specify attributes of a DKIM key, including the public key itself. Every DKIM record must begin with the version tag \"v\". Its value must be \"DKIM1\". More versions may be added in the future. All other tags are optional and may appear in any order. DKIM is case-insensitive. Either upper- or lower-case may be used.", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The Internet Assigned Numbers Authority (IANA) maintains the official "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://www.iana.org/assignments/dkim-parameters/dkim-parameters.xhtml",
    rel: "noopener noreferrer"
  }, "registry of DKIM tags"),
  /*#__PURE__*/_createTextVNode(". The current set of DKIM tags are:")
], -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"v\""),
    /*#__PURE__*/_createTextVNode(" Version. The value must be \"DKIM1\". This tag must appear first in every DKIM record.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"k\""),
    /*#__PURE__*/_createTextVNode(" Key type. Most often \"rsa\", which is the default, but \"ed25519\" was added in "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://datatracker.ietf.org/doc/html/rfc8032",
      rel: "noopener noreferrer"
    }, "RFC 8032"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"p\""),
    /*#__PURE__*/_createTextVNode(" Public key data. This tag is required in every DKIM record. It contains the public key in base64 format.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"h\""),
    /*#__PURE__*/_createTextVNode(" Acceptable hash algorithms. A colon-separated list of the hash algorithms that might be used to sign messages.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"n\""),
    /*#__PURE__*/_createTextVNode(" Notes. Human-readable text containing notes for administrators.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"s\""),
    /*#__PURE__*/_createTextVNode(" Service type. The type of service this key may be used for, may be \"*\" for all services or \"email\". Not to be confused with the \"s\" tag that can appear in the "),
    /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
    /*#__PURE__*/_createTextVNode(" field of an email header!")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "\"t\""),
    /*#__PURE__*/_createTextVNode(" Flags. A colon-separated list of flags. Valid flag values are \"y\" and \"s\". Use \"y\" to indicate that DKIM is in testing mode. Use \"s\" to indicate that the domain in the "),
    /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
    /*#__PURE__*/_createTextVNode(" must exactly match the domain the message was sent from (and must not be a subdomain).")
  ])
], -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("h2", null, "Finding DKIM records for a domain", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "Like SPF and DMARC, DKIM records are stored as TXT records in the DNS. So the dig or nslookup command line tools with query type TXT can be used.", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, "Unlike SPF and DMARC, however, a little more information is needed to know what DNS name to use for the TXT lookup because DKIM often uses selectors.", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("You can use the \"show original\" option in Gmail (or a similar option in your mail client) to view a raw email message and find the "),
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(". Here is an example from a recent email sent by Google:")
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("pre", null, "DKIM-Signature: v=1; a=rsa-sha256; c=relaxed/relaxed;\nd=google.com; s=20210112;\nh=to:from:subject:message-id:list-id:feedback-id:list-unsubscribe\n:precedence:reply-to:date:mime-version:from:to:cc:subject:date;\nbh=20sEX7VfkwWYovzA9s67DASRk/vrLDfnoMIxPqE3RWY=;\nb=dOpodEh2qKx2TzLIxvtjCG4NHbdWHUcAC4pw018ABPS2rEGXCIhBD5nbX0kUMrLJ6E\nNMCX7p5qW+HfBghAxWTk1q2dWbnMNtalpBYFgNdxgU6DtEuwiuZIrMGJUl32pfgttbzq\n/CnNS6z6nLkEoUWXpwXNMBmYhPpC7/26MFF324A6l71bAKUQDfb/QNWsYyAuRwNHnHnS\nCgTB1dERPMdgl9YJb6PiB3UIV8PlOsA3jYq1AHzMEICBaqK/Qr6SfwNA6x0gwkdfszlt\nLMwNVR8i7/VZkUjSHEZPbB8tYiQedg6oW+0eCB/GYia2V3qqFIfudg3pYAYkG+jugbtZ\nHVmg==", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The "),
  /*#__PURE__*/_createElementVNode("strong", null, "DKIM-Signature"),
  /*#__PURE__*/_createTextVNode(" is long and includes many fields, including the header fields that were signed and a digital signature of the message.")
], -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("But only the \"s\" and \"d\" tags are needed to find the DKIM record. The \"s\" tag gives the selector and the \"d\" tag gives the domain. So in this example, the DKIM selector is "),
  /*#__PURE__*/_createElementVNode("code", null, "20210112"),
  /*#__PURE__*/_createTextVNode(" and the DKIM domain is "),
  /*#__PURE__*/_createElementVNode("code", null, "google.com"),
  /*#__PURE__*/_createTextVNode(". Combine these two strings with "),
  /*#__PURE__*/_createElementVNode("code", null, "_domainkey"),
  /*#__PURE__*/_createTextVNode(" in between to determine the DNS lookup name for the DKIM record: "),
  /*#__PURE__*/_createElementVNode("code", null, "20210112._domainkey.google.com"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, "This name can be looked up using dig with query type TXT to find the DKIM record for this message:", -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("pre", null, "dig +short txt 20210112._domainkey.google.com\n\"v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAtnbQAeqvEP2pG2L540W9JvVU7qy767Zs83Hjw34PCkZ9/9dWE6cK+CYaMNIQqTGfwq4uUe3diBuz3Uikkr+WPMj9AuxtxJqUAO8PsZ2Y5DneFpz5kVesC9/rtXgCwgYOmO\" \"9UjSy4IN11ewXUBuCH+zp2v5Abv5T0Lol/nWl8wLgRI1IesstingY4cnSfo3Pq3U0I1GAxdNFCK2FPedPpg4sdPpHqtxVvRLMLamRKoUfySBMjpXuMNL0UeCizmFfdUL73ZdiS+MNxGECzFNmeCngFBscLQN++urvfB9OqHQrbxLIwNyni3KMbE/E+cxPkx4KxhyGHSU2klV1vvIAHfwIDAQAB\"", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you could use the following:", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=TXT 20210112._domainkey.google.com.", -1)
const _hoisted_43 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_44 = /*#__PURE__*/_createElementVNode("h2", null, "You may also need DMARC and SPF", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("p", null, "DKIM is only part of the story for securing email. There are three pillars of email security:", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Domain-keys Identified Mail (DKIM)"),
  /*#__PURE__*/_createTextVNode(": DKIM uses public key cryptography to validate the sender's authenticity and to protect email messages from tampering.")
], -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("p", null, "While adding DKIM to your domain is a good start, consider adding SPF and DMARC to protect your domain and brand from abuse.", -1)
const _hoisted_48 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_49 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_50 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_51 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How does DKIM prevent man-in-the-middle attacks? ", -1)
const _hoisted_52 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "A man-in-the-middle (MITM) attack occurs when a malicious party is able to observe and alter network traffic between a sender and receiver on the Internet. DKIM adds cryptographic signatures to email so that any tampering can be detected.")
], -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("p", null, "In email, a MITM attacker intercepts TCP/IP packets as they flow from the sending mail server to the receiving mail server. The attacker would attempt to modify the contents of the packets and change the email as it travels across the Internet.", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("p", null, "The digital signature added by DKIM allows the receiving mail server to verify that an attacker did not alter the message after the sending mail server signed the message. MITM attacks are one reason why many Internet protocols have been upgraded to use cryptographic signatures.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import { learningBreadcrumb, emailBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'Email and the DNS',
  url: '/learning/dkim-a-practical-guide/',
  breadcrumbs: [learningBreadcrumb, emailBreadcrumb],
  title: 'DKIM: A practical guide',
  description: 'DomainKeys Identified Mail (DKIM) adds public key cryptography to email. Email is signed by the sender, and verified by the receiver using the DKIM record.',
  overviewDescription: 'DomainKeys Identified Mail (DKIM) adds public key cryptography to email. Email is signed by the sender, and verified by the receiver using the DKIM record.',
  overviewImage: '/img/learning/dkim-a-practical-guide/dns-dkim-records-small.jpg',
  overviewImageAlt: 'DKIM in the DNS',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dkim-a-practical-guide/the-dkim-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dkim-a-practical-guide/the-dkim-record-social.jpg',
  written: new Date('2022-11-14T12:32:48.825Z'),
  updated: new Date('2023-03-28T11:58:48.710Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'EmailSecurityDkim',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/txt/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dkim-a-practical-guide/dns-dkim-records.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _createElementVNode("p", null, [
          _createTextVNode("DKIM does not include a policy to require that every email from a domain is signed. So with DKIM alone, spammers can still send forged email from your domain. This is where "),
          _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
            default: _withCtx(() => [
              _createTextVNode("Domain-based Message Authentication, Reporting, and Conformance (DMARC)")
            ]),
            _: 1
          }),
          _createTextVNode(" steps in.")
        ]),
        _hoisted_11,
        _createVNode(Callout, {
          icon: "info",
          title: "Can a domain have multiple DKIM records?",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_12,
            _hoisted_13,
            _hoisted_14,
            _hoisted_15
          ]),
          _: 1
        }),
        _hoisted_16,
        _hoisted_17,
        _createElementVNode("p", null, [
          _createTextVNode("DKIM records are stored as TXT records, although a "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/cname/" }, {
            default: _withCtx(() => [
              _createTextVNode("CNAME record")
            ]),
            _: 1
          }),
          _createTextVNode(" is sometimes used as an intermediate \"hop\". We will discuss how CNAMEs can be used in DKIM a bit later.")
        ]),
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _createVNode(Callout, {
          icon: "info",
          title: "How are CNAME records used in DKIM?",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", null, [
              _createElementVNode("strong", null, [
                _createTextVNode("DKIM records are stored in TXT records, but an intermediate "),
                _createVNode(_component_router_link, { to: "/learning/dns-record-types/cname/" }, {
                  default: _withCtx(() => [
                    _createTextVNode("CNAME record")
                  ]),
                  _: 1
                }),
                _createTextVNode(" is often used.")
              ])
            ]),
            _hoisted_21,
            _hoisted_22
          ]),
          _: 1
        }),
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_26
          ]),
          _: 1
        }),
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _hoisted_32,
        _hoisted_33,
        _hoisted_34,
        _hoisted_35,
        _hoisted_36,
        _hoisted_37,
        _hoisted_38,
        _hoisted_39,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_40
          ]),
          _: 1
        }),
        _hoisted_41,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_42
          ]),
          _: 1
        }),
        _createElementVNode("p", null, [
          _createTextVNode("Once you have the selector and domain, you can also find the DKIM record using our "),
          _createVNode(_component_router_link, { to: "/txt-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("TXT lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool, or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_43, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find TXT records",
            onSubmitted: redirectTo
          })
        ]),
        _hoisted_44,
        _hoisted_45,
        _createElementVNode("ul", null, [
          _createElementVNode("li", null, [
            _createElementVNode("strong", null, [
              _createVNode(_component_router_link, { to: "/learning/spf-a-practical-guide/" }, {
                default: _withCtx(() => [
                  _createTextVNode("Sender Policy Framework (SPF)")
                ]),
                _: 1
              })
            ]),
            _createTextVNode(": SPF ensures that only authorized mail servers can send mail for a domain.")
          ]),
          _hoisted_46,
          _createElementVNode("li", null, [
            _createElementVNode("strong", null, [
              _createVNode(_component_router_link, { to: "/learning/dmarc-a-practical-guide/" }, {
                default: _withCtx(() => [
                  _createTextVNode("Domain-based Message Authentication, Reporting, and Conformance (DMARC)")
                ]),
                _: 1
              })
            ]),
            _createTextVNode(": DMARC adds policy and reporting. It prevents spammers from abusing the Return-Path field to evade SPF and enforces DKIM-signed email. DMARC also allows administrators to control how messages that fail SPF and DKIM checks should be treated.")
          ])
        ]),
        _hoisted_47,
        _createElementVNode("p", null, [
          _createTextVNode("Read my next article to learn "),
          _createVNode(_component_router_link, { to: "/learning/mx-vs-spf-vs-dmarc-vs-dkim-vs-bimi/" }, {
            default: _withCtx(() => [
              _createTextVNode("the difference between MX, SPF, DKIM, DMARC and BIMI")
            ]),
            _: 1
          }),
          _createTextVNode(".")
        ]),
        _createElementVNode("div", _hoisted_48, [
          _hoisted_49,
          _createElementVNode("div", _hoisted_50, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_51,
                    _createElementVNode("span", _hoisted_52, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_53,
                    _hoisted_54,
                    _hoisted_55
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})